<template>
  <q-page padding class="bg-grey-1">
    <div class="row q-mb-md">
      <div class="text-h5 text-weight-bold text-primary">My Teams</div>
    </div>

    <div v-if="teams.length === 0" class="flex flex-center column q-mt-xl text-grey-6">
      <q-icon name="sports_baseball" size="64px" class="q-mb-sm" />
      <div class="text-h6">No teams found</div>
      <div>Create your first team to get started!</div>
    </div>

    <q-list v-else bordered separator class="bg-white rounded-borders shadow-1">
      <q-item v-for="team in teams" :key="team.id" clickable v-ripple @click="promptTeamRole(team)">
        <q-item-section avatar>
          <q-avatar color="primary" text-color="white">
            {{ team.name.charAt(0).toUpperCase() }}
          </q-avatar>
        </q-item-section>

        <q-item-section>
          <q-item-label class="text-weight-medium">{{ team.name }}</q-item-label>
          <q-item-label caption>
            {{ team.players ? team.players.length : 0 }} Players
          </q-item-label>
        </q-item-section>

        <q-item-section side>
          <q-icon name="chevron_right" color="grey" />
        </q-item-section>
      </q-item>
    </q-list>

    <q-page-sticky position="bottom-right" :offset="[18, 18]">
      <q-btn
        fab
        icon="add"
        color="secondary"
        @click="showCreateModal = true"
        aria-label="Create New Team"
      />
    </q-page-sticky>

    <q-dialog v-model="showCreateModal" persistent>
      <q-card style="min-width: 350px">
        <q-card-section class="bg-primary text-white">
          <div class="text-h6">Create New Team</div>
        </q-card-section>

        <q-card-section class="q-pt-md">
          <q-input
            v-model="newTeamName"
            label="Team Name"
            outlined
            autofocus
            @keyup.enter="saveTeam(true)"
            :rules="[(val) => !!val || 'Team name is required']"
          />
          <div class="q-mt-md">
            <div class="text-caption text-grey-8 q-mb-sm">Game Role</div>
            <q-btn-toggle
              v-model="newTeamRole"
              spread
              no-caps
              unelevated
              toggle-color="primary"
              color="white"
              text-color="primary"
              class="border-primary"
              style="border: 1px solid var(--q-primary)"
              :options="[
                { label: 'Home', value: 'home' },
                { label: 'Visitor', value: 'visiting' },
              ]"
            />
          </div>
        </q-card-section>

        <q-card-actions align="right" class="text-primary q-pa-md">
          <div class="row q-gutter-sm justify-end full-width">
            <q-btn flat label="Cancel" v-close-popup color="grey-8" />

            <q-btn outline label="Save" @click="saveTeam(false)" :disable="!newTeamName" />

            <q-btn
              unelevated
              color="primary"
              label="Save & Set Lineup"
              icon-right="arrow_forward"
              @click="saveTeam(true)"
              :disable="!newTeamName"
            />
          </div>
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useNotify } from 'src/helpers/notify'
import { useQuasar } from 'quasar'
import { db } from 'src/models/db'
import type { Team } from 'src/stores/base-types'

const router = useRouter()
const $q = useQuasar()

import { useGameStore } from 'stores/game-store' // Your Pinia store
const gameStore = useGameStore()

// State
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const teams = ref<any[]>([]) // Replace 'any' with your actual 'Team' type from base-types.ts
const showCreateModal = ref(false)
const newTeamName = ref('')
const newTeamRole = ref<'home' | 'visiting'>('home')

// Load teams on mount
onMounted(async () => {
  await loadTeams()
})

async function loadTeams() {
  try {
    // 1. Fetch teams from Dexie.js
    teams.value = await db.teams.toArray()
  } catch (error) {
    console.error('Failed to load teams:', error)
    useNotify('Could not load teams.', null, 'negative')
  }
}

function promptTeamRole(team: Team) {
  $q.bottomSheet({
    message: `Select role for the ${team.name}`,
    actions: [
      {
        label: 'Home Team',
        icon: 'home',
        id: 'home',
        color: 'primary',
      },
      {
        label: 'Visiting Team',
        icon: 'flight_takeoff',
        id: 'visiting',
        color: 'secondary',
      },
    ],
  })
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    .onOk(async (action) => {
      // 'action.id' holds the string we defined above ('home' or 'visiting')
      await selectTeam(team, action.id as 'home' | 'visiting')
    })
    .onCancel(() => {
      // If they click off the bottom sheet or swipe down, do nothing
    })
}

async function selectTeam(team: Team, role: 'home' | 'visiting') {
  // 1. Set the active team in Pinia so the rest of the app knows who is playing
  gameStore.setActiveTeam(team, role)
  // 2. Route the user to the lineup configuration screen
  await router.push({ name: 'SetLineup' })
}

// Handles creating a new team
async function saveTeam(routeToLineup: boolean) {
  if (!newTeamName.value) return

  try {
    // 1. Build the object WITHOUT an ID
    const teamToSave: Team = {
      name: newTeamName.value,
      players: [],
    }

    // 2. Add it to Dexie.
    // Dexie automatically returns the newly generated ++id integer!
    const newTeamId = await db.teams.add(teamToSave)
    teams.value.push(teamToSave)
    useNotify('Team created successfully! ' + newTeamId.toString(), 1000, 'positive')

    // 3. Reset the modal
    showCreateModal.value = false
    newTeamName.value = ''

    // 4. Branching Logic based on which button was clicked
    if (routeToLineup) {
      // Passes the newly generated ID directly into your select function
      await selectTeam(teamToSave, newTeamRole.value)
    }

    // Reset the modal
    showCreateModal.value = false
    newTeamName.value = ''
    newTeamRole.value = 'home' // Reset to default
  } catch (error) {
    console.error('Failed to create team:', error)
    useNotify('Failed to create team.', null, 'negative')
  }
}
</script>
